import { useContext, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { OrdensContext } from 'contexts/OrdensContext';
import Container from 'components/Container';
import Header from 'components/Header';
import moment from 'moment';

const intervalos = [
  { value: 15, label: '15 segundos' },
  { value: 30, label: '30 segundos' },
  { value: 60, label: '1 minuto' },
  { value: 120, label: '2 minutos' },
  { value: 300, label: '5 minutos' },
];

const numero = (value, percent = false) => {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? `${parsed.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}${percent ? '%' : ''}`
    : '—';
};

const data = (value, comHora = false) => {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid()
    ? parsed.format(comHora ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY')
    : '—';
};

const normalizarMapa = (response) => {
  const rows = Array.isArray(response) ? response : response?.data;
  if (!Array.isArray(rows)) throw new Error('Formato inválido do mapa');
  return rows.map((row) => {
    const processos = row.Processos ?? row.processos ?? [];
    const itens =
      typeof processos === 'string' ? JSON.parse(processos) : processos;
    if (!Array.isArray(itens))
      throw new Error('Formato inválido dos processos');
    return { ...row, processos: itens };
  });
};

const corApontamento = (value = '') => {
  if (value.startsWith('EM PRODU')) return 'success';
  if (value.startsWith('PARADO')) return 'error';
  if (value.startsWith('PAUSADO')) return 'warning';
  return 'default';
};

const Campo = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
      {children ?? '—'}
    </Typography>
  </Box>
);

const Processo = ({ item, resumido = false }) => (
  <Paper
    variant="outlined"
    sx={{
      p: resumido ? 1.5 : 2,
      minWidth: 0,
      width: '100%',
      boxSizing: 'border-box',
      borderRadius: 2,
      borderLeft: 4,
      borderLeftColor:
        corApontamento(item.apontamento) === 'default'
          ? 'grey.400'
          : `${corApontamento(item.apontamento)}.main`,
    }}
  >
    <Stack spacing={resumido ? 1 : 1.5}>
      <Box sx={{ minWidth: 0 }}>
        {!resumido && (
          <Typography variant="caption" color="text.secondary">
            Ref. {item.referencia || '—'}
          </Typography>
        )}
        <Tooltip title={item.produto || 'Produto sem descrição'} arrow>
          <Typography fontWeight={600} noWrap>
            {item.produto || 'Produto sem descrição'}
          </Typography>
        </Tooltip>
        {!resumido && (
          <Typography variant="body2" color="text.secondary">
            {item.linha || 'Linha não informada'}
          </Typography>
        )}
      </Box>
      <Chip
        size="small"
        color={corApontamento(item.apontamento)}
        label={item.apontamento || 'NAO INICIADO'}
        sx={{
          alignSelf: 'flex-start',
          height: 'auto',
          maxWidth: '100%',
          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
        }}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <Campo label="Quantidade">
          {numero(item.quantidade)}
          {item.unidade_medida ? ` ${item.unidade_medida}` : ''}
        </Campo>
        {!resumido && <Campo label="Processo">{item.processo || '—'}</Campo>}
        <Campo label="Nº do Documento">{item.documento}</Campo>
        {!resumido && (
          <>
            <Campo label="Data de Emissão">{data(item.dtemissao, true)}</Campo>
            <Campo label="Lote de Cálculo">{item.lote}</Campo>
            <Campo label="Data do Cálculo">{data(item.calculo, true)}</Campo>
          </>
        )}
      </Box>
      {!resumido && (
        <>
          <Divider />
          <Campo label="Operadores">
            {item.operador || 'Nenhum operador informado'}
          </Campo>
        </>
      )}
    </Stack>
  </Paper>
);

const Mapa = () => {
  const { getMapa, getLoading } = useContext(OrdensContext);
  const [mapa, setMapa] = useState([]);
  const [intervalo, setIntervalo] = useState(60);
  const [versao, setVersao] = useState('completa');
  const [atualizadoEm, setAtualizadoEm] = useState(null);
  const [erro, setErro] = useState(false);
  const emAndamento = useRef(false);
  const atualizarRef = useRef(null);
  const contextRef = useRef({ getMapa, getLoading });

  useEffect(() => {
    contextRef.current = { getMapa, getLoading };
  }, [getMapa, getLoading]);

  useEffect(() => {
    let ativo = true;
    const atualizar = async () => {
      if (emAndamento.current || contextRef.current.getLoading) return;
      emAndamento.current = true;
      try {
        let recebido = false;
        await contextRef.current.getMapa({
          cb: (response) => {
            if (!ativo) return;
            const resultado = normalizarMapa(response);
            setMapa(resultado);
            setAtualizadoEm(new Date());
            setErro(false);
            recebido = true;
          },
        });
        if (!ativo) return;
        if (!recebido) setErro(true);
      } catch (error) {
        if (ativo) setErro(true);
      } finally {
        emAndamento.current = false;
      }
    };
    atualizarRef.current = atualizar;
    atualizar();
    return () => {
      ativo = false;
      atualizarRef.current = null;
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => atualizarRef.current?.(), intervalo * 1000);
    return () => clearInterval(timer);
  }, [intervalo]);

  const total = mapa.reduce((sum, setor) => sum + setor.processos.length, 0);

  return (
    <Container fillHeight sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Header titulo="Mapa da Produção" />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        gap={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            {mapa.length} centros de custo · {total} itens pendentes
          </Typography>
          <Typography variant="caption" color="text.secondary" role="status">
            {getLoading
              ? 'Atualizando mapa…'
              : atualizadoEm
                ? `Última atualização: ${moment(atualizadoEm).format('DD/MM/YYYY HH:mm:ss')}`
                : 'Aguardando atualização'}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          gap={1.5}
          alignItems={{ sm: 'center' }}
        >
          <ToggleButtonGroup
            value={versao}
            exclusive
            size="small"
            color="primary"
            aria-label="Versão dos cartões"
            onChange={(_, value) => {
              if (value !== null) setVersao(value);
            }}
          >
            <ToggleButton value="resumida">Resumida</ToggleButton>
            <ToggleButton value="completa">Completa</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            select
            size="small"
            label="Atualizar a cada"
            value={intervalo}
            onChange={(event) => setIntervalo(Number(event.target.value))}
            sx={{ minWidth: 190 }}
          >
            {intervalos.map((opcao) => (
              <MenuItem key={opcao.value} value={opcao.value}>
                {opcao.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>
      <Box sx={{ height: 4, flexShrink: 0, mb: 1 }}>
        {getLoading && <LinearProgress />}
      </Box>
      {erro && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Não foi possível atualizar o mapa.{' '}
          {atualizadoEm ? 'Os últimos dados recebidos foram mantidos. ' : ''}Uma
          nova tentativa será feita automaticamente.
        </Alert>
      )}
      {!atualizadoEm && getLoading ? (
        <Stack flex={1} alignItems="center" justifyContent="center" gap={2}>
          <CircularProgress />
          <Typography color="text.secondary">
            Carregando mapa da produção…
          </Typography>
        </Stack>
      ) : !mapa.length ? (
        !erro && (
          <Alert severity="info">
            Nenhum item pendente de produção no momento.
          </Alert>
        )
      ) : (
        <Box
          aria-label="Kanban da produção"
          sx={{
            display: 'flex',
            gap: 2,
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowX: 'auto',
            pb: 1,
            alignItems: 'stretch',
          }}
        >
          {mapa.map((setor, index) => (
            <Paper
              key={`${setor.ccusto}-${index}`}
              variant="outlined"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: '0 0 340px',
                width: 340,
                minWidth: 0,
                maxWidth: { xs: '85vw', sm: 340 },
                boxSizing: 'border-box',
                minHeight: 0,
                bgcolor: 'grey.50',
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderTop: 4,
                  borderColor: 'primary.main',
                  borderRadius: '8px 8px 0 0',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                >
                  <Typography
                    component="h2"
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {setor.ccusto || 'Centro de custo'}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${setor.processos.length} itens`}
                  />
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1,
                    mt: 1.5,
                  }}
                >
                  <Campo label="Eficiência">
                    {numero(setor.eficiencia, true)}
                  </Campo>
                  <Campo label="Utilização">
                    {numero(setor.utilizacao, true)}
                  </Campo>
                  <Campo label="Disponibilidade">
                    {numero(setor.disponibilidade, true)}
                  </Campo>
                </Box>
              </Box>
              <Divider />
              <Stack
                spacing={1.5}
                sx={{ p: 1.5, overflowY: 'auto', minHeight: 0 }}
              >
                {setor.processos.map((item, itemIndex) => (
                  <Processo
                    key={`${item.documento}-${item.referencia}-${itemIndex}`}
                    item={item}
                    resumido={versao === 'resumida'}
                  />
                ))}
              </Stack>
            </Paper>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default Mapa;
